/**
 * transfer-errors.ts — Typed error class for the BitDrop application layer.
 *
 * ── SEPARATION OF CONCERNS ─────────────────────────────────────────────────────
 * TransferError codes are application/domain-level identifiers.
 * HTTP semantics (404, 410, 429, 5xx) from ShortUrlError are MAPPED here
 * by resolve-receive-link.ts — the core never directly imports HTTP status codes.
 *
 * ── UI MAPPING ────────────────────────────────────────────────────────────────
 * The error `message` field is always user-facing safe text.
 * The `code` field is for programmatic handling, not display.
 */

export type TransferErrorCode =
  | 'INVALID_LINK'                 // Input failed classification
  | 'ENGINE_NOT_INITIALIZED'       // startReceiveSession before engine ready
  | 'DOWNLOAD_FAILED'              // Engine-level download failure
  | 'UPLOAD_FAILED'                // Engine-level upload failure
  | 'SESSION_NOT_FOUND'            // Operation on unknown sessionId
  | 'SHORT_URL_NOT_FOUND'          // Short URL resolved to 404 / expired
  | 'SHORT_URL_EXPIRED'            // Short URL explicitly deleted (410)
  | 'SHORT_URL_SERVICE_DOWN'       // 429 or 5xx from shortener
  | 'SHORT_URL_NETWORK_ERROR'      // fetch() threw (offline)
  | 'SHORT_URL_INVALID_TARGET';    // Server returned non-magnet target

export class TransferError extends Error {
  constructor(
    public readonly code: TransferErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'TransferError';
  }
}
